import React, { useState } from 'react';
import PropTypes from 'prop-types';
import * as BS from 'react-bootstrap';
import Column from '../../../models/SpreadsheetReportConfigs/Column';
import Panel from '../../../models/SpreadsheetReportConfigs/Panel';
import * as Resource from '../../../apis/SpreadsheetReportConfigs';
import { convertCamelCaseKeys } from '../../../lib/objectUtils';

const scope =
  'frontend.components.SpreadsheetReportConfigs.Modal.PreviewOutputDataModal';

const propTypes = {
  panel: PropTypes.exact(Panel.propTypes),
  columns: PropTypes.arrayOf(PropTypes.shape(Column.propTypes)),
};

const PreviewOutputTableDataModal = ({ panel, columns }) => {
  const [show, setShow] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  const hasPreviewData = Array.isArray(previewData) && previewData.length > 0;
  const dataRowCount = Array.isArray(previewData)
    ? panel.showHeaderRow
      ? Math.max(previewData.length - 1, 0)
      : previewData.length
    : 0;
  const shouldShowEmptyMessage =
    Array.isArray(previewData) && dataRowCount === 0;
  const isMaxRowsEmpty =
    panel.numberOfMaxLines === undefined ||
    panel.numberOfMaxLines === null ||
    panel.numberOfMaxLines === '';
  const isPreviewDisabled = columns.length === 0 || isMaxRowsEmpty;

  const onHide = () => {
    setShow(false);
  };

  const handlePreview = () => {
    const matchResult = location.pathname.match(/\/projects\/(\d+)/);
    if (!matchResult) {
      return;
    }
    const [, projectId] = matchResult;
    Resource.getPanelPreviewData(
      projectId,
      {
        panels: panel,
        isPreview: true,
      },
      I18n.locale
    ).then((response) => {
      const convertedCamelCaseData = convertCamelCaseKeys(response.data);
      setShow(true);
      setPreviewData(convertedCamelCaseData.previewData);
    });
  };

  return (
    <>
      <BS.Button
        bsStyle='primary'
        onClick={() => handlePreview()}
        disabled={isPreviewDisabled}
      >
        {I18n.t(`${scope}.action.preview`)}
      </BS.Button>

      <BS.Modal show={show} onHide={onHide} bsSize='large'>
        <BS.Modal.Header closeButton>
          <BS.Modal.Title>{I18n.t(`${scope}.header.title`)}</BS.Modal.Title>
        </BS.Modal.Header>

        <BS.Modal.Body>
          <div>
            <div style={{ overflowX: 'auto', width: '100%' }}>
              <BS.Table striped bordered style={{ overflowX: 'auto' }}>
                <>
                  {hasPreviewData && (
                    <>
                      {panel.showHeaderRow ? (
                        <>
                          <thead>
                            <tr>
                              {previewData[0].map((column, columnIndex) => (
                                <th
                                  key={`row-0-column-${columnIndex}`}
                                  style={{ whiteSpace: 'nowrap' }}
                                >
                                  {column}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          {previewData.length > 1 && (
                            <tbody>
                              {previewData.slice(1).map((row, rowIndex) => (
                                <tr key={`row-${rowIndex + 1}`}>
                                  {row.map((value, valueIndex) => (
                                    <td
                                      key={`row-${
                                        rowIndex + 1
                                      }-column-${valueIndex}-value`}
                                    >
                                      {[
                                        'thumbnail_image_img',
                                        'original_image_img',
                                      ].includes(
                                        columns[valueIndex]?.content
                                      ) ? (
                                        <img src={value} width={100} />
                                      ) : (
                                        value
                                      )}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          )}
                        </>
                      ) : (
                        <tbody>
                          {previewData.map((row, rowIndex) => (
                            <tr key={`row-${rowIndex}`}>
                              {row.map((value, valueIndex) => (
                                <td
                                  key={`row-${rowIndex}-column-${valueIndex}-value`}
                                >
                                  {[
                                    'thumbnail_image_img',
                                    'original_image_img',
                                  ].includes(columns[valueIndex]?.content) ? (
                                    <img src={value} width={100} />
                                  ) : (
                                    value
                                  )}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      )}
                    </>
                  )}
                </>
              </BS.Table>
            </div>
            {shouldShowEmptyMessage && (
              <BS.Alert bsStyle='danger'>
                {I18n.t(`${scope}.message.emptyData`)}
              </BS.Alert>
            )}
            <div>{I18n.t(`${scope}.message.limitRows`)}</div>
          </div>
        </BS.Modal.Body>
      </BS.Modal>
    </>
  );
};

PreviewOutputTableDataModal.propTypes = propTypes;

export default PreviewOutputTableDataModal;
